<template>
  <nav ref="nav" class="bottom-nav" :aria-label="$t('common.nav.label')" :class="{ 'on-dark': isLandingPage, 'sheet-open': moreOpen }">
    <router-link :to="$lp('/explore')" class="bottom-nav-item" active-class="active">
      <i class="fas fa-map"></i>
      <span class="bottom-nav-label">{{ $t('common.nav.explore') }}</span>
    </router-link>
    <router-link :to="$lp('/submit')" class="bottom-nav-item" active-class="active">
      <i class="fas fa-camera"></i>
      <span class="bottom-nav-label">{{ $t('common.nav.submit') }}</span>
    </router-link>
    <button ref="moreBtn" type="button" class="bottom-nav-item more-item" :class="{ active: moreOpen || inMore }"
      aria-haspopup="dialog" :aria-expanded="moreOpen ? 'true' : 'false'" aria-controls="more-sheet" @click="toggleMore">
      <i :class="moreOpen ? 'fas fa-xmark' : 'fas fa-ellipsis'"></i>
      <span class="bottom-nav-label">{{ moreOpen ? $t('common.nav.close') : $t('common.nav.more') }}</span>
    </button>
  </nav>
  <MoreSheet ref="sheet" :open="moreOpen" @close="closeMore" />
</template>

<style scoped>
/* Fresh Mint floating nav: surface pill, mint active item with dark ink. */
.bottom-nav {
  display: flex;
  gap: 4px;
  justify-content: space-between;
  align-items: center;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: min(calc(100% - 32px), 420px);
  box-sizing: border-box;
  padding: 10px 12px;
  z-index: 100;
  box-shadow: var(--shadow-floating);
  border-radius: var(--radius-xl);
}

.bottom-nav-item {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: var(--color-secondary);
  min-height: var(--touch-min);
  padding: 8px 14px;
  border-radius: var(--radius-lg);
  transition: background-color 0.2s ease, color 0.2s ease;
}

.bottom-nav-item { position: relative; z-index: 1; }
button.bottom-nav-item { background: none; border: 0; font: inherit; cursor: pointer; }
/* While the sheet is open, "Schließen" carries the highlight and the page item looks inactive. */
.bottom-nav.sheet-open .bottom-nav-item.active:not(.more-item),
.bottom-nav.sheet-open .bottom-nav-item.active:not(.more-item) .bottom-nav-label { color: var(--color-secondary); font-weight: 400; }
.bottom-nav.sheet-open:not(.slide-ready) .bottom-nav-item.active:not(.more-item) { background: none; }

/* The mint pill slides to the active item. Until it is placed, the active item paints it itself. */
.bottom-nav::before {
  content: '';
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 0;
  width: var(--ind-w, 0);
  transform: translateX(var(--ind-x, 0));
  opacity: var(--ind-o, 0);
  background: var(--color-primary); /* mint fill with dark ink; selection also shows in bold text, no border */
  border-radius: var(--radius-lg);
  pointer-events: none;
}
.bottom-nav.slide-ready::before {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease;
}

.bottom-nav-item.active { color: var(--color-on-primary); }
.bottom-nav:not(.slide-ready) .bottom-nav-item.active {
  background: var(--color-primary);
}
.bottom-nav-item.active i { animation: nav-pop 0.3s ease; }
@keyframes nav-pop { 0% { transform: scale(0.85); } 60% { transform: scale(1.12); } 100% { transform: scale(1); } }

.bottom-nav-item.active .bottom-nav-label {
  color: var(--color-on-primary);
  font-weight: 600;
}

.bottom-nav-item i {
  margin-bottom: 2px;
  font-size: 20px;
}

.bottom-nav-label {
  font-size: 12px;
  font-weight: 400;
  transition: color 0.2s ease;
}

.bottom-nav-item:not(.active):hover {
  background: var(--color-subtle);
}

/* Over the dark landing page the nav is always dark, whatever the theme. It sets its own tokens, so
   text, hover and borders stay readable in light and dark mode. */
.bottom-nav.on-dark {
  background-color: var(--color-fixed-dark-surface);
  border-color: var(--color-fixed-dark-border);
}
.bottom-nav.on-dark .bottom-nav-item:not(.active) { color: var(--color-fixed-dark-text-muted); }
.bottom-nav.on-dark .bottom-nav-item:not(.active):hover { background: var(--color-fixed-dark-hover); color: var(--color-fixed-dark-text); }

@media (forced-colors: active) {
  .bottom-nav::before { border: 2px solid Highlight; box-sizing: border-box; }
}

@media (prefers-reduced-motion: reduce) {
  .bottom-nav-item { transition: none; }
  .bottom-nav.slide-ready::before { transition: none; }
  .bottom-nav-item.active i { animation: none; }
}
</style>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';
import { placeIndicator } from '@/utils/slidingIndicator';
import { useRoute } from 'vue-router';
import MoreSheet from '@/components/MoreSheet.vue';

export default {
  components: { MoreSheet },
  setup() {
    const route = useRoute();
    const isLandingPage = computed(() => route.name === 'LandingPage');
    
    const nav = ref(null);
    let ro = null;
    const moreOpen = ref(false);
    const moreBtn = ref(null);
    // pages that live in the Mehr sheet keep "Mehr" highlighted
    const MORE_ROUTES = ['Profile', 'Settings', 'About', 'Accessibility', 'Imprint', 'Privacy', 'Cities',
      'City', 'CityReports', 'CityMap', 'CityKit', 'CityExport', 'CitySettings'];
    const inMore = computed(() => MORE_ROUTES.includes(route.name));
    const sel = () => (moreOpen.value || inMore.value ? '.more-item' : 'a.bottom-nav-item.active');
    const place = () => placeIndicator(nav.value, sel());
    const toggleMore = () => { moreOpen.value = !moreOpen.value; };
    const closeMore = () => {
      if (!moreOpen.value) return;
      moreOpen.value = false;
      nextTick(() => { if (moreBtn.value && document.activeElement === document.body) moreBtn.value.focus(); });
    };
    watch(moreOpen, () => { nextTick(() => place()); });
    // Esc and the Tab trap work from the sheet and from the nav's "Schließen" button
    const onKey = (e) => {
      if (!moreOpen.value) return;
      if (e.key === 'Escape') { e.preventDefault(); closeMore(); if (moreBtn.value) moreBtn.value.focus(); }
      else if (e.key === 'Tab' && sheet.value) sheet.value.trap(e);
    };
    const sheet = ref(null);
    onMounted(() => document.addEventListener('keydown', onKey));
    onBeforeUnmount(() => document.removeEventListener('keydown', onKey));
    onMounted(() => { nextTick(() => place()); ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => place()); if (ro) ro.observe(nav.value); });
    onBeforeUnmount(() => { if (ro) ro.disconnect(); });
    watch(() => route.fullPath, () => { moreOpen.value = false; place(); }, { flush: 'post' }); // post flush: router-link classes are already updated

    return {
      isLandingPage,
      nav,
      moreOpen,
      inMore,
      moreBtn,
      sheet,
      toggleMore,
      closeMore,
    };
  },
};

</script>
